import { useCallback, useId, useState, type KeyboardEvent } from "react";
import { useTranslation } from "react-i18next";
import { X } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import {
  MAX_INVOICE_EMAIL_ADDRESSES,
  isValidInvoiceEmailSegment,
} from "@/utils/invoice-email-addresses";

export function InvoiceEmailAddressListInput(props: {
  value: string[];
  onChange: (next: string[]) => void;
  disabled?: boolean;
  placeholder?: string;
  id?: string;
  "aria-invalid"?: boolean;
  className?: string;
}): JSX.Element {
  const {
    value,
    onChange,
    disabled = false,
    placeholder,
    id,
    "aria-invalid": ariaInvalid,
    className,
  } = props;
  const { t } = useTranslation();
  const generatedId = useId();
  const inputId = id ?? `invoice-email-tags-${generatedId}`;
  const [draft, setDraft] = useState("");
  const [localError, setLocalError] = useState<string | null>(null);

  const tryAdd = useCallback((): void => {
    const raw = draft.trim();
    if (!raw) return;
    if (value.length >= MAX_INVOICE_EMAIL_ADDRESSES) {
      setLocalError(
        t("clients.form.invoiceEmailMaxReached", {
          count: MAX_INVOICE_EMAIL_ADDRESSES,
        })
      );
      return;
    }
    if (!isValidInvoiceEmailSegment(raw)) {
      setLocalError(t("clients.form.invoiceEmailInvalidChip"));
      return;
    }
    const key = raw.toLowerCase();
    if (value.some((e) => e.trim().toLowerCase() === key)) {
      setDraft("");
      setLocalError(null);
      return;
    }
    onChange([...value, raw.trim()]);
    setDraft("");
    setLocalError(null);
  }, [draft, onChange, t, value]);

  const removeAt = useCallback(
    (index: number): void => {
      onChange(value.filter((_, i) => i !== index));
      setLocalError(null);
    },
    [onChange, value]
  );

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === "Enter") {
      e.preventDefault();
      tryAdd();
      return;
    }
    if (e.key === "Backspace" && draft === "" && value.length > 0) {
      e.preventDefault();
      removeAt(value.length - 1);
    }
  };

  const hint =
    value.length >= MAX_INVOICE_EMAIL_ADDRESSES
      ? t("clients.form.invoiceEmailMaxReached", {
          count: MAX_INVOICE_EMAIL_ADDRESSES,
        })
      : t("clients.form.invoiceEmailAddHint");

  return (
    <div className={cn("space-y-2", className)}>
      <div
        className={cn(
          "flex min-h-10 flex-wrap items-center gap-2 rounded-md border border-input bg-white dark:bg-[var(--dk-surface)] px-2 py-1.5 text-sm ring-offset-background transition-colors duration-150 motion-reduce:transition-none",
          "focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2",
          ariaInvalid === true && "border-destructive",
          disabled && "cursor-not-allowed opacity-70"
        )}
      >
        {value.map((email, index) => (
          <Badge
            key={`${email}-${index}`}
            variant="secondary"
            className="gap-1 pr-1 font-normal transition-colors duration-150 motion-reduce:transition-none"
          >
            <span className="max-w-[220px] truncate sm:max-w-xs">{email}</span>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-5 w-5 shrink-0 rounded-full p-0 text-muted-foreground hover:text-foreground"
              onClick={() => removeAt(index)}
              disabled={disabled}
              aria-label={t("clients.form.invoiceEmailRemoveTag", { email })}
            >
              <X className="h-3 w-3" />
            </Button>
          </Badge>
        ))}
        <Input
          id={inputId}
          type="text"
          inputMode="email"
          autoComplete="email"
          disabled={disabled}
          value={draft}
          onChange={(e) => {
            setDraft(e.target.value);
            setLocalError(null);
          }}
          onKeyDown={onKeyDown}
          onBlur={() => {
            if (draft.trim()) tryAdd();
          }}
          placeholder={
            placeholder ?? t("clients.form.invoiceEmailPlaceholder")
          }
          className="min-w-[12rem] flex-1 border-0 bg-transparent p-1 shadow-none focus-visible:ring-0 focus-visible:ring-offset-0"
          aria-invalid={ariaInvalid === true || localError != null}
          aria-describedby={`${inputId}-hint`}
        />
      </div>
      <p id={`${inputId}-hint`} className="text-xs text-muted-foreground">
        {hint}
      </p>
      {localError != null && (
        <p className="text-sm text-destructive" role="alert">
          {localError}
        </p>
      )}
    </div>
  );
}
